/**
 * RouteErrorBoundary — the router-level error element.
 *
 * Two cases:
 *  - **Stale chunk / dynamic-import failure** (the app was rebuilt in dev or
 *    redeployed, so a lazy route's chunk hash changed under the open tab). This
 *    is expected during "we're making changes" — show a calm *maintenance*
 *    banner and auto-reload once to fetch the fresh build (guarded against reload
 *    loops). The operator never sees a scary stack for a routine update.
 *  - **Any other route error** — a generic "Something went wrong" card with the
 *    message, a Reload, and a way back to the dashboard.
 */
import { useEffect } from 'react'
import { useRouteError } from 'react-router-dom'

/** A lazy-import / code-split chunk failed to load (usually a stale build). */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null
  const name = e?.name ?? ''
  const msg  = (e?.message ?? String(error ?? '')).toLowerCase()
  return (
    name === 'ChunkLoadError' ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('failed to load module script')
  )
}

const RELOAD_KEY = 'stride:chunk-reload-at'
const RELOAD_DEBOUNCE_MS = 10_000

export function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError()
  const stale = isChunkLoadError(error)

  // Auto-reload once on a stale-chunk error to pull the fresh build — but never
  // tighter than the debounce, so a genuinely broken chunk can't loop forever.
  useEffect(() => {
    if (!stale) return
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
    if (Date.now() - last > RELOAD_DEBOUNCE_MS) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
      window.location.reload()
    }
  }, [stale])

  if (stale) {
    return (
      <div data-testid="route-error-stale" className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
        <div className="grid h-11 w-11 place-items-center rounded-card bg-accent-soft text-accent">
          <span className="animate-pulse text-lg" aria-hidden>↻</span>
        </div>
        <h2 className="text-base font-semibold text-fg">Updating Stride…</h2>
        <p className="max-w-sm text-sm text-fg-muted">
          A new version is available — reloading to pick it up. If this screen sticks, reload manually.
        </p>
        <button
          type="button" onClick={() => window.location.reload()} data-testid="route-error-reload"
          className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-fg hover:bg-accent-600"
        >Reload now</button>
      </div>
    )
  }

  const message = error instanceof Error ? error.message : 'An unexpected error occurred.'
  return (
    <div data-testid="route-error" className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
      <div className="grid h-11 w-11 place-items-center rounded-card bg-del-soft text-del">
        <span className="text-lg" aria-hidden>!</span>
      </div>
      <h2 className="text-base font-semibold text-fg">Something went wrong</h2>
      <p data-testid="route-error-message" className="max-w-md break-words text-sm text-fg-muted">{message}</p>
      <div className="mt-1 flex items-center gap-2">
        <button
          type="button" onClick={() => window.location.reload()} data-testid="route-error-reload"
          className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-fg hover:bg-accent-600"
        >Reload</button>
        <a href="/dashboard" className="rounded-control border border-border px-4 py-2 text-sm font-semibold text-fg-muted hover:bg-surface-2">
          Back to dashboard
        </a>
      </div>
    </div>
  )
}
